import { Controller } from "@hotwired/stimulus"

// Keeps the inbox message thread pinned to the newest message, the way a chat
// app should:
//   - jump to the bottom on load (no animation),
//   - follow new messages while you're already at the bottom,
//   - stay put — and surface a "new messages" pill — if you've scrolled up to
//     read history, so a live arrival doesn't yank you down,
//   - always snap to the bottom on your own send.
//
// Lives on the right panel so it can own both the scroll container and the
// pill. Prepended history (lazy-load, SCH-359) inserts nodes before existing
// ones, which we deliberately ignore here so loading old messages never
// scrolls you away.
export default class extends Controller {
  static targets = ["scroller", "list", "pill", "loader"]
  static values = {
    threshold: { type: Number, default: 120 },
    olderUrl: String,
    oldestId: Number,
    hasMore: Boolean
  }

  connect() {
    if (!this.hasScrollerTarget || !this.hasListTarget) return

    this.atBottom = true
    this.forceScroll = false
    this.loadingOlder = false

    this.onScroll = () => { this.trackPosition(); this.maybeLoadOlder() }
    this.scrollerTarget.addEventListener("scroll", this.onScroll, { passive: true })

    // Follow appends (live broadcast + optimistic send).
    this.observer = new MutationObserver((mutations) => this.onMutations(mutations))
    this.observer.observe(this.listTarget, { childList: true })

    // Keep the last message visible when the composer grows or the window
    // resizes, but only while we're already stuck to the bottom.
    this.resizeObserver = new ResizeObserver(() => { if (this.atBottom) this.scrollToBottom(false) })
    this.resizeObserver.observe(this.scrollerTarget)

    // A send should always drop us to the newest message, even if we'd
    // scrolled up. Flag it before the optimistic bubble lands.
    this.onSubmit = (event) => {
      const form = event.target
      if (form && form.matches && form.matches('form[action*="/messages/send"]')) this.forceScroll = true
    }
    document.addEventListener("turbo:submit-start", this.onSubmit)

    this.scrollToBottom(false)
  }

  disconnect() {
    if (this.onScroll) this.scrollerTarget.removeEventListener("scroll", this.onScroll)
    this.observer?.disconnect()
    this.resizeObserver?.disconnect()
    if (this.onSubmit) document.removeEventListener("turbo:submit-start", this.onSubmit)
  }

  trackPosition() {
    this.atBottom = this.distanceFromBottom() <= this.thresholdValue
    if (this.atBottom) this.hidePill()
  }

  distanceFromBottom() {
    const el = this.scrollerTarget
    return el.scrollHeight - el.scrollTop - el.clientHeight
  }

  onMutations(mutations) {
    // Only react to nodes appended at the END of the list. Prepended history
    // (mutation.nextSibling != null) must not move the viewport.
    const appended = mutations.some((m) =>
      m.addedNodes.length > 0 &&
      m.nextSibling === null &&
      Array.from(m.addedNodes).some((n) => n.nodeType === Node.ELEMENT_NODE)
    )
    if (!appended) return

    if (this.forceScroll || this.atBottom) {
      this.scrollToBottom(true)
    } else {
      this.showPill()
    }
    this.forceScroll = false
  }

  // Reverse infinite scroll: when the user nears the top and older messages
  // exist, fetch the next older page and prepend it, anchoring scroll position
  // so the viewport doesn't jump.
  maybeLoadOlder() {
    if (this.loadingOlder || !this.hasMoreValue || !this.olderUrlValue) return
    if (this.scrollerTarget.scrollTop > this.thresholdValue) return
    this.loadOlder()
  }

  async loadOlder() {
    this.loadingOlder = true
    this.showLoader()

    try {
      const url = `${this.olderUrlValue}&before=${this.oldestIdValue}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error(`older page request failed: ${response.status}`)
      const data = await response.json()

      if (data.html) {
        // Anchor on the current scroll height so the prepend leaves the
        // reader exactly where they were.
        const previousHeight = this.scrollerTarget.scrollHeight
        const previousTop = this.scrollerTarget.scrollTop
        this.listTarget.insertAdjacentHTML("afterbegin", data.html)
        this.scrollerTarget.scrollTop = previousTop + (this.scrollerTarget.scrollHeight - previousHeight)
      }

      if (data.oldest_id) this.oldestIdValue = data.oldest_id
      this.hasMoreValue = !!data.has_more
    } catch (error) {
      // Leave hasMore on so a later scroll can retry; just log it.
      console.error(error)
    } finally {
      this.hideLoader()
      this.loadingOlder = false
    }
  }

  showLoader() {
    if (this.hasLoaderTarget) this.loaderTarget.classList.replace("hidden", "flex")
  }

  hideLoader() {
    if (this.hasLoaderTarget) this.loaderTarget.classList.replace("flex", "hidden")
  }

  scrollToBottom(smooth) {
    const el = this.scrollerTarget
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" })
    this.atBottom = true
    this.hidePill()
  }

  // Pill click handler.
  jumpToBottom() {
    this.scrollToBottom(true)
  }

  showPill() {
    if (this.hasPillTarget) this.pillTarget.classList.remove("hidden")
  }

  hidePill() {
    if (this.hasPillTarget) this.pillTarget.classList.add("hidden")
  }
}
